import React from 'react';
import { pageStructuredData, videoStructuredData } from '@surfline/web-common';
import { spotReportPath } from '../../utils/urls';
import MetaTags from '../MetaTags';
import config from '../../config';
import type { Camera } from '../../types/camera';
import type { SpotReport } from '../../types/spot';

const title = (spotName: string, cameras: Array<Camera>, multiCam: boolean) => {
  if (multiCam) {
    return `${spotName} Surf Report, Surf Forecast and Surf Cams - Surfline`;
  }
  if (cameras && cameras[0]) {
    return `${spotName} Surf Report, Surf Forecast and Surf Cam - Surfline`;
  }
  return `${spotName} Surf Report and Surf Forecast - Surfline`;
};

const description = (
  spotName: string,
  countryName: string,
  cameras: Array<Camera>,
  multiCam: boolean,
  report: SpotReport['report'],
) => {
  const hasCamera = cameras && cameras[0];
  if (multiCam) {
    return `Surf report, surf forecast and surf cams for ${spotName}, ${countryName}. Including 16-day surf forecast for surf height, swell, wind and tide conditions.`;
  }
  if (hasCamera) {
    return `Surf report, surf forecast and surf cam for ${spotName}, ${countryName}. Including 16-day surf forecast for surf height, swell, wind and tide conditions.`;
  }
  if (hasCamera && report) {
    return `Surf report, surf forecast, surf cam and surf observation for ${spotName}, ${countryName}. Including 16-day surf forecast for surf height, swell, wind and tide conditions.`;
  }

  return `Surf report and surf forecast for ${spotName}, ${countryName}. Including 16-day surf forecast for surf height, swell, wind and tide conditions.`;
};

const urlPath = (spotId: string, spotName: string) =>
  spotReportPath({}, { _id: spotId, name: spotName });
const image = (cameras: Array<Camera>) => cameras?.[0]?.control;

interface Props {
  cameras?: Array<Camera>;
  metaDescription?: string;
  multiCam?: boolean;
  report?: SpotReport['report'];
  spotId: string;
  spotName: string;
  countryName: string;
  titleTag?: string;
}

const SpotPageMeta: React.FC<Props> = ({
  cameras = [],
  metaDescription: customDescription = null,
  multiCam = false,
  report = null,
  spotId,
  spotName,
  countryName,
  titleTag = null,
}) => {
  const hasCamera = cameras && cameras[0];
  const pageTitle = titleTag || title(spotName, cameras, multiCam);
  const dynamicDescription = description(spotName, countryName, cameras, multiCam, report);
  const metaDescription = customDescription || dynamicDescription;
  const metaImage = image(cameras);
  const camDescription = `Watch the latest ${spotName} surf conditions at Surfline.com`;
  const url = `${config.surflineHost}${urlPath(spotId, spotName)}`;

  // Midnight of current day
  const currentDay = new Date();
  const startOfCurrentDayUTC = new Date(currentDay.setUTCHours(0, 0, 0, 0));
  const endOfCurrentDayUTC = new Date(currentDay.setUTCHours(24, 0, 0, 0));

  const fallbackDate = startOfCurrentDayUTC;

  const reportDate = report?.timestamp ? new Date(report.timestamp) : fallbackDate;

  const camStructuredDataName = `${spotName} Surf Cam`;

  return (
    <MetaTags
      title={pageTitle}
      description={metaDescription}
      urlPath={urlPath(spotId, spotName)}
      image={metaImage}
    >
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: pageStructuredData({
            url,
            headline: pageTitle,
            image: metaImage,
            description: metaDescription,
            author: report?.forecaster?.name || 'Surfline Forecast Team',
            cssSelector: '.sl-current-conditions',
            dateModified: reportDate,
          }),
        }}
      />

      {hasCamera && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: videoStructuredData(
              camStructuredDataName,
              spotName,
              camDescription,
              metaImage,
              startOfCurrentDayUTC,
              startOfCurrentDayUTC,
              endOfCurrentDayUTC,
              `${config.surflineHost}${urlPath(spotId, spotName)}`,
            ),
          }}
        />
      )}
    </MetaTags>
  );
};

export default SpotPageMeta;
